import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { formatDate, formatAddressLine, fmtBaht } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RECEIVE_METHOD_LABELS = {
  delivery: 'ส่งพัสดุ',
  pickup: 'ไปรับเอง',
  lalamove: 'Lalamove',
};

const fmtAmount = (n) =>
  Number.isFinite(Number(n)) ? fmtBaht(Number(n)) : String(n ?? '-');

const LiffSubmissions = () => {
  const shopName = useSelector((state) => state.shop.shopName) || 'smile';
  const [submissions, setSubmissions] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [imageModal, setImageModal] = useState(null);
  const [manualReviewModal, setManualReviewModal] = useState(null);
  const [rejectNote, setRejectNote] = useState({});
  const [updatingReceiptId, setUpdatingReceiptId] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/liff-admin/submissions`, {
        params: { shop: shopName, status: filter },
        withCredentials: true,
      });
      setSubmissions(res.data?.submissions || []);
    } catch (err) {
      toast.error('โหลดข้อมูลไม่สำเร็จ: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [shopName, filter]);

  const fetchReceipts = useCallback(async () => {
    setReceiptsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/liff-admin/receipts`, {
        params: { shop: shopName, status: 'all' },
        withCredentials: true,
      });
      setReceipts(res.data?.receipts || []);
    } catch (err) {
      toast.error('โหลดสลิปไม่สำเร็จ: ' + (err.response?.data?.message || err.message));
    } finally {
      setReceiptsLoading(false);
    }
  }, [shopName]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);
  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const pendingManualReceipts = receipts.filter((r) => r.isManualCheck && !r.liffManualCheckStatus);
  const hasAnyUnreviewedReceipts = submissions.some((s) => s.hasUnreviewedLiffReceipts);

  const getReceiptsForSubmission = (submission) => {
    if (process.env.NODE_ENV === 'development') {
      try {
        // Lightweight debug logging to help inspect ID mapping
        // (will only appear in browser console in dev)
        // eslint-disable-next-line no-console
        console.log('[LIFF Admin] submission', {
          id: submission?.id,
          liffReceiptIds: submission?.liffReceiptIds,
        });
        // eslint-disable-next-line no-console
        console.log(
          '[LIFF Admin] receipts ids',
          receipts.map((r) => r.id)
        );
      } catch (_e) {
        // ignore logging errors
      }
    }
    if (!submission?.liffReceiptIds || submission.liffReceiptIds.length === 0 || receipts.length === 0) {
      // Fallback: match by customerId when liffReceiptIds is empty (e.g. seed data or legacy records)
      const customerId = submission?.customer?.id;
      if (!customerId) return [];
      return receipts.filter((r) => r.customer?.id === customerId);
    }
    const idSet = new Set(
      submission.liffReceiptIds
        .map((rid) => Number(rid))
        .filter((n) => Number.isFinite(n))
    );
    if (idSet.size === 0) return [];
    const byId = new Map(
      receipts.map((r) => [Number(r.id), r])
    );
    const result = [];
    idSet.forEach((id) => {
      const found = byId.get(id);
      if (found) result.push(found);
    });
    return result;
  };

  const handleToggle = async (id, isHandled) => {
    try {
      const endpoint = isHandled ? 'unhandle' : 'handle';
      await axios.patch(
        `${BACKEND_URL}/api/liff-admin/submissions/${id}/${endpoint}`,
        {},
        { withCredentials: true }
      );
      toast.success(isHandled ? 'ยกเลิกสถานะแล้ว' : 'ดำเนินการแล้ว');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleReceiptStatusUpdate = async (receiptId, status, note) => {
    setUpdatingReceiptId(receiptId);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/liff-admin/receipts/${receiptId}/status`,
        { status, note: note || undefined },
        { withCredentials: true }
      );
      if (status === 'approved') {
        toast.success('อนุมัติแล้ว — ระบบจะแจ้งลูกค้าอัตโนมัติตามรอบถัดไป');
      } else {
        toast.warn('ปฏิเสธสลิปแล้ว — กรุณาแจ้งลูกค้าด้วยตนเอง');
      }
      setRejectNote((prev) => ({ ...prev, [receiptId]: '' }));
      setManualReviewModal(null);
      fetchReceipts();
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setUpdatingReceiptId(null);
    }
  };

  const pendingCount = submissions.filter((s) => !s.adminHandledAt).length;

  const formatThaiShortDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('th-TH', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });
  };

  const handleCopy = async (s) => {
    try {
      const shopLabel = (s.customer?.shop || shopName || '').toLowerCase() === 'tama' ? 'Tama' : 'Smile';
      const customerDisplay =
        s.customer?.contact ||
        s.customer?.smileName ||
        '-';

      const pickupDateLabel = s.lalamovePickupDate
        ? formatThaiShortDate(s.lalamovePickupDate)
        : formatThaiShortDate(s.receiveMethodDate);

      const firstLine = `คุณ ${customerDisplay} (${shopLabel}) เรียกรถ ${pickupDateLabel || '-'} ${s.lalamovePickupTime || ''}`.trim();

      const boxGroups = Array.isArray(s.boxGroups) ? s.boxGroups : [];
      const roundBaseDate =
        boxGroups.length > 0 ? boxGroups[0].date : s.receiveMethodDate || null;

      const roundLine =
        roundBaseDate && boxGroups.length <= 1
          ? `รอบ ${new Date(roundBaseDate).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit' })}`
          : '';

      const boxNameLine =
        s.customer?.smileName ? `ชื่อกล่อง ${shopLabel} ${s.customer.smileName}` : '';

      const boxLines = [];
      boxGroups.forEach((g, groupIndex) => {
        const d = new Date(g.date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).toString().padStart(2, '0');
        const dateLabel = `${day}/${month}`;
        if (Array.isArray(g.boxNos) && g.boxNos.length > 0) {
          const prefix = groupIndex === 0 ? 'เลขกล่อง ' : '';
          boxLines.push(`${prefix}${g.boxNos.join(' ')} (${dateLabel})`);
        }
      });

      const boxLine = `รวม ${s.boxCount || 0} กล่อง`;
      const namePhoneLine = s.address ? `${s.address.name || ''}\n${s.address.phone || ''}`.trim() : '';
      const addrLine = s.address ? formatAddressLine(s.address) : '';
      const linkLine = s.address?.locationLink || '';

      const parts = [
        firstLine, '', roundLine, boxNameLine, ...boxLines, boxLine,
        '', namePhoneLine, addrLine, '', linkLine,
      ].filter((block) => block && String(block).trim() !== '');

      await navigator.clipboard.writeText(parts.join('\n'));
      toast.success('คัดลอกข้อความไปยังคลิปบอร์ดแล้ว');
    } catch (err) {
      toast.error('คัดลอกไม่สำเร็จ: ' + (err.message || 'ไม่ทราบสาเหตุ'));
    }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-800">รายการจาก LIFF</h1>
        <button
          onClick={() => { fetchSubmissions(); fetchReceipts(); }}
          disabled={loading}
          className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors"
        >
          {loading ? 'กำลังโหลด...' : 'รีเฟรช'}
        </button>
      </div>

      {/* Alert: need to review receipts before processing */}
      {hasAnyUnreviewedReceipts && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3">
          <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="font-medium text-amber-800">มีสลิปโอนเงินที่รอตรวจสอบ</p>
            <p className="text-sm text-amber-700 mt-1">
              กรุณาอนุมัติ/ปฏิเสธสลิปในคอลัมน์ "สลิป" ของแต่ละแถวก่อนกดดำเนินการแล้ว
            </p>
          </div>
        </div>
      )}

      {/* Submissions filter tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'pending', label: 'รอดำเนินการ' },
          { key: 'handled', label: 'ดำเนินการแล้ว' },
          { key: 'all', label: 'ทั้งหมด' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
              filter === tab.key
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {tab.label}
            {tab.key === 'pending' && filter !== 'pending' && pendingCount > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">กำลังโหลด...</div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">ไม่มีรายการ</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">สถานะ</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">วันที่</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">ลูกค้า</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">วิธีรับ</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">ขนส่ง</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">ที่อยู่</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">กล่อง</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">หมายเหตุ</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">สลิป / ตรวจสอบ</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">คัดลอก</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">จัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {submissions.map((s) => {
                const isHandled = !!s.adminHandledAt;
                return (
                  <tr key={s.id} className={isHandled ? 'bg-green-50/50' : s.hasUnreviewedLiffReceipts ? 'bg-amber-50/50' : ''}>
                    <td className="px-4 py-3">
                      {isHandled ? (
                        <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          แล้ว
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          รอ
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(s.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{s.customer?.smileName || '-'}</div>
                      <div className="text-xs text-gray-500">{s.customer?.contact || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{RECEIVE_METHOD_LABELS[s.receiveMethod] || s.receiveMethod}</td>
                    <td className="px-4 py-3 text-gray-700">{s.courierTh || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs max-w-xs truncate" title={formatAddressLine(s.address)}>
                      {s.address ? (
                        <>
                          <div>{s.address.name} {s.address.phone}</div>
                          <div className="truncate">{formatAddressLine(s.address)}</div>
                        </>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{s.boxCount}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs max-w-[160px]">
                      {s.remark ? (
                        <span className="inline-block bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 rounded text-xs">{s.remark}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {(() => {
                        const subReceipts = getReceiptsForSubmission(s);
                        if (subReceipts.length === 0) return <span className="text-gray-400 text-xs">-</span>;
                        return (
                          <div className="flex flex-col gap-2">
                            {subReceipts.map((r) => (
                              <div
                                key={r.id}
                                className={`text-xs border rounded-lg p-2 min-w-0 ${r.isManualCheck && !r.liffManualCheckStatus ? 'bg-amber-50 border-amber-200' : r.liffManualCheckStatus === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  {r.imageUrl && r.imageUrl !== 'pending_upload' ? (
                                    <button
                                      type="button"
                                      onClick={() => setImageModal({ url: r.imageUrl, id: r.id })}
                                      className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 hover:bg-blue-200 font-medium"
                                    >
                                      ดูสลิป
                                    </button>
                                  ) : null}
                                  <span className="font-medium text-gray-800">{fmtAmount(r.amount)} บาท</span>
                                  {r.isManualCheck ? (
                                    !r.liffManualCheckStatus ? (
                                      <span className="text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">รอตรวจ</span>
                                    ) : r.liffManualCheckStatus === 'approved' ? (
                                      <span className="text-green-700">อนุมัติแล้ว</span>
                                    ) : (
                                      <span className="text-red-700" title={r.liffManualCheckNote || ''}>ปฏิเสธ</span>
                                    )
                                  ) : (
                                    <span className="text-gray-500">Auto</span>
                                  )}
                                </div>
                                {r.isManualCheck && !r.liffManualCheckStatus && (
                                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setManualReviewModal({
                                          id: r.id,
                                          imageUrl: r.imageUrl,
                                          amount: r.amount,
                                          customer: r.customer,
                                          paymentDateTime: r.paymentDateTime,
                                        });
                                      }}
                                      className="px-2 py-0.5 text-xs font-medium rounded bg-amber-500 text-white hover:bg-amber-600"
                                    >
                                      ตรวจ / แก้ไข
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleCopy(s)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      >
                        คัดลอก
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => handleToggle(s.id, isHandled)}
                        disabled={!isHandled && !!s.hasUnreviewedLiffReceipts}
                        title={!isHandled && s.hasUnreviewedLiffReceipts ? 'กรุณาตรวจสอบและอนุมัติ/ปฏิเสธสลิปในคอลัมน์สลิปก่อน' : ''}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          !isHandled && s.hasUnreviewedLiffReceipts
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed border border-gray-300'
                            : isHandled
                              ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                              : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {isHandled ? 'ยกเลิก' : 'ดำเนินการแล้ว'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Image modal */}
      {imageModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setImageModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label="ดูรูปสลิป"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-2 flex justify-between items-center border-b">
              <span className="text-sm font-medium text-gray-700">สลิปโอนเงิน #{imageModal.id}</span>
              <button type="button" onClick={() => setImageModal(null)} className="p-1 rounded hover:bg-gray-100 text-gray-600">ปิด</button>
            </div>
            <div className="p-4 overflow-auto flex-1 flex justify-center">
              <img src={imageModal.url} alt="สลิปโอนเงิน" className="max-w-full h-auto object-contain" onClick={(e) => e.stopPropagation()} />
            </div>
          </div>
        </div>
      )}

      {/* Manual review modal */}
      {manualReviewModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setManualReviewModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label="ตรวจสอบสลิป"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 flex justify-between items-center border-b">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  ตรวจสอบสลิป #{manualReviewModal.id}
                </p>
                <p className="text-xs text-gray-500">
                  ลูกค้า: {manualReviewModal.customer?.smileName || '-'} | ยอด {fmtAmount(manualReviewModal.amount)} บาท
                </p>
              </div>
              <button
                type="button"
                onClick={() => setManualReviewModal(null)}
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
              >
                ปิด
              </button>
            </div>
            <div className="flex flex-1 overflow-hidden">
              <div className="w-1/2 border-r bg-gray-50 flex items-center justify-center p-3">
                {manualReviewModal.imageUrl ? (
                  <img
                    src={manualReviewModal.imageUrl}
                    alt="สลิปโอนเงิน"
                    className="max-w-full max-h-full object-contain rounded-lg border border-gray-200 bg-white"
                  />
                ) : (
                  <span className="text-gray-400 text-sm">ไม่มีรูปสลิป</span>
                )}
              </div>
              <div className="w-1/2 p-4 flex flex-col">
                <p className="text-sm text-gray-700 mb-3">
                  ตรวจสอบรูปสลิปทางซ้าย หากถูกต้องให้กด <span className="font-medium text-green-700">อนุมัติ</span>{' '}
                  หากไม่ถูกต้องให้ใส่เหตุผลแล้วกด <span className="font-medium text-red-700">ปฏิเสธ</span>
                </p>
                <label className="text-xs font-medium text-gray-700 mb-1">
                  หมายเหตุ (ถ้าปฏิเสธ)
                </label>
                <textarea
                  rows={4}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1 mb-4 resize-none"
                  placeholder="ตัวอย่าง: ยอดไม่ตรง / เวลาชำระไม่อยู่ในรอบ / สลิปไม่ชัดเจน ฯลฯ"
                  value={rejectNote[manualReviewModal.id] || ''}
                  onChange={(e) =>
                    setRejectNote((prev) => ({ ...prev, [manualReviewModal.id]: e.target.value }))
                  }
                />
                <div className="mt-auto flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setManualReviewModal(null)}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="button"
                    disabled={updatingReceiptId === manualReviewModal.id}
                    onClick={() => handleReceiptStatusUpdate(manualReviewModal.id, 'rejected', rejectNote[manualReviewModal.id])}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    ปฏิเสธ
                  </button>
                  <button
                    type="button"
                    disabled={updatingReceiptId === manualReviewModal.id}
                    onClick={() => handleReceiptStatusUpdate(manualReviewModal.id, 'approved')}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    อนุมัติ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiffSubmissions;
